import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { dropdownItemPropType } from '../components/EventSchedule/propTypes.js';
import useOutsideClickDetector from '../utils/useOutsideClickDetector.jsx';

const getCurrentTextFromItems = (items, selectedItems) => {
  const finalSelectedItems = typeof selectedItems === 'string' ? [selectedItems] : selectedItems;

  return items.filter((item) => finalSelectedItems.includes(item.id)).map((item) => item.label).join(', ');
};

function Dropdown({
  changeHandler, defaultText = '', id, items, selectedItems = [], withCheckboxes = false,
}) {
  const wrapperRef = useRef(null);

  const [currentText, setCurrentText] = useState(defaultText || getCurrentTextFromItems(items, selectedItems) || 'Select Item');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (selectedItems.length > 0) setCurrentText(getCurrentTextFromItems(items, selectedItems));
  }, [selectedItems]);

  useOutsideClickDetector(wrapperRef, () => setIsExpanded(false));

  return (
    <div
      aria-controls={id}
      aria-expanded={isExpanded}
      aria-haspopup
      className={`c-dropdown ${isExpanded ? 'c-dropdown--expanded' : ''}`}
      ref={wrapperRef}
      role="combobox"
    >
      <span
        className="c-dropdown__item"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
      >
        {currentText}
      </span>

      <ul
        className={`c-dropdown__items ${isExpanded ? 'c-dropdown__items--expanded' : ''}`}
        id={id}
        role="listbox"
      >
        {items.map((item) => {
          const isSelected = selectedItems.includes(item.id);

          const clickHandler = (itemId) => {
            if (!withCheckboxes && !isSelected) setIsExpanded(false);

            changeHandler(itemId);
          };

          if (withCheckboxes) {
            return (
              <li
                aria-checked={isSelected}
                className={`c-dropdown__item c-dropdown__item--with-checkbox ${isSelected ? 'is-selected' : ''}`}
                key={item.id}
                onClick={() => clickHandler(item.id)}
                onKeyDown={() => clickHandler(item.id)}
                role="menuitemcheckbox"
                tabIndex={0}
              >
                {item.label}
              </li>
            );
          }

          return (
            <li
              aria-selected={withCheckboxes ? undefined : isSelected}
              className={`c-dropdown__item c-dropdown__item--no-checkbox ${isSelected ? 'is-selected' : ''}`}
              key={item.id}
              onClick={() => clickHandler(item.id)}
              onKeyDown={() => clickHandler(item.id)}
              role="option"
              tabIndex={0}
            >
              {item.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

Dropdown.propTypes = {
  changeHandler: PropTypes.func.isRequired,
  defaultText: PropTypes.string,
  id: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(dropdownItemPropType).isRequired,
  selectedItems: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ])),
    PropTypes.string,
  ]),
  withCheckboxes: PropTypes.bool,
};

export default Dropdown;
